import { useEffect, useState } from "react";
import { Loader2, Save, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";
import { useFunder } from "@/funder/FunderContext";

function FunderEditor() {
  const { refresh } = useFunder();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then((r) => setForm({ funder_name: r.data.funder_name || "", funder_abbr: r.data.funder_abbr || "" }))
      .catch(() => setForm({ funder_name: "", funder_abbr: "" }));
  }, []);

  const save = async () => {
    const abbr = form.funder_abbr.trim();
    if (!abbr) {
      setError("Abbreviation is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.put("/settings", { funder_name: form.funder_name.trim() || abbr, funder_abbr: abbr });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (form === null) return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="funder-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="funder-name">Full name</Label>
          <Input
            id="funder-name"
            data-testid="funder-name-input"
            value={form.funder_name}
            onChange={(e) => setForm({ ...form, funder_name: e.target.value })}
            placeholder="e.g. Animal & Society Advocacy"
          />
        </div>
        <div>
          <Label htmlFor="funder-abbr">Abbreviation</Label>
          <Input
            id="funder-abbr"
            data-testid="funder-abbr-input"
            value={form.funder_abbr}
            onChange={(e) => setForm({ ...form, funder_abbr: e.target.value })}
            placeholder="e.g. ACSA"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
        <div className="ml-auto">
          <Button size="sm" onClick={save} disabled={saving} data-testid="funder-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save funder
          </Button>
        </div>
      </div>
    </div>
  );
}

export function FunderCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Funder</CardTitle>
        <CardDescription>The organization-level funder. Its abbreviation is used for every funder label across the app and for new invoice numbers. Already-issued invoices keep their original numbers.</CardDescription>
      </CardHeader>
      <CardContent><FunderEditor /></CardContent>
    </Card>
  );
}
